import { Link } from 'react-router-dom';
import { LegalLayout } from '../components/LegalLayout';
import '../legal.css';

export function SecurityPage() {
  return (
    <LegalLayout
      title="Security"
      updated="June 1, 2026"
      summary="How Rynora is built, hosted, and defended — and how to report something you find."
      needsReview={false}
    >
      <p>
        For procurement / compliance review (SOC 2, ISO, DPA, sub-processor list, AI Bill of Materials, SLA), go
        straight to the <Link to="/trust">Trust Center</Link>. This page covers the operational and engineering
        controls; the Trust Center is the contractual / commercial counterpart.
      </p>

      <h2>Reporting a vulnerability</h2>
      <p>
        Email <a href="mailto:ops@rynora.app">ops@rynora.app</a> with the details. We aim to acknowledge in 24 hours,
        triage in 72 hours, and ship a fix as fast as the severity warrants. Please give us a chance to fix before you
        publish, and please don't run invasive tests against production accounts you don't own.
      </p>
      <p>
        Our machine-readable security policy lives at <a href="/.well-known/security.txt">/.well-known/security.txt</a>{' '}
        (RFC 9116).
      </p>

      <h2>Hosting</h2>
      <ul>
        <li>
          Every Rynora SaaS organisation runs in our <strong>US-PHX</strong> region today (Oracle Cloud Infrastructure,
          us-phoenix-1). An EU-Frankfurt region is in design-partner alpha — see{' '}
          <Link to="/trust/regions">/trust/regions</Link> for the rolling status.
        </li>
        <li>Single-tenant Oracle Cloud Always Free instance, locked to specific source IPs at the firewall.</li>
        <li>Caddy in front terminates TLS, sets HSTS / CSP / X-Frame-Options on every response.</li>
        <li>Cloudflare in front of that for DNS, WAF, and bot mitigation.</li>
        <li>QA, UAT, and Production each run in isolated Docker networks with their own Postgres volume.</li>
      </ul>

      <h2>Authentication</h2>
      <ul>
        <li>Passwords are hashed with argon2id.</li>
        <li>
          JWTs are signed with a per-environment secret. Hardened environments fail fast if the secret is missing.
        </li>
        <li>MFA via TOTP is available on every plan.</li>
        <li>SSO via SAML 2.0 / OIDC is available on the Enterprise tier.</li>
      </ul>

      <h2>Authorization</h2>
      <p>
        Every API request is scoped to a user, organization, and role. Admin-only paths are guarded by an explicit
        middleware, not by route obscurity.
      </p>

      <h2>Data at rest</h2>
      <ul>
        <li>Postgres volumes are LUKS-encrypted at the host level.</li>
        <li>Backups are encrypted client-side before upload to Backblaze B2.</li>
        <li>API tokens are stored hashed; we only show the plain text once at creation.</li>
        <li>
          Application-level AES-256-GCM envelope encryption for every persistently-stored secret value (integration
          tokens, BYOK LLM keys, project secrets). Enterprise orgs can enrol their own AES-256 Key Encryption Key (BYOK
          / CMK) from <code>/dashboard/security</code>, rotate it on demand, and crypto-shred the wrapped Data
          Encryption Key after a configurable grace window — every lifecycle event is forwarded to the customer's SIEM
          webhook.
        </li>
      </ul>

      <h2>Data in transit</h2>
      <ul>
        <li>TLS 1.3 enforced via Caddy + Cloudflare.</li>
        <li>
          HSTS with <code>includeSubDomains</code> and <code>preload</code>.
        </li>
        <li>Webhook URLs must be HTTPS in hardened environments.</li>
      </ul>

      <h2>Operations</h2>
      <ul>
        <li>Sentry for backend + frontend error tracking.</li>
        <li>Better Stack for uptime checks and the public status page.</li>
        <li>Grafana Cloud for metrics, with alerts mirrored to a Discord channel.</li>
        <li>Daily encrypted backups, weekly restore drills.</li>
      </ul>

      <h2>Disclosure history</h2>
      <p>
        Public disclosure timelines and post-mortems are published in the{' '}
        <a href="https://github.com/rynoratech/rynora/security/advisories" rel="noreferrer" target="_blank">
          security advisories page
        </a>{' '}
        once a fix is shipped.
      </p>
    </LegalLayout>
  );
}
